import { useState } from "react";
import type { SampleRow, Station } from "../lib/types";
import { DEPTH_COLOR, DEPTHS } from "../lib/types";
import { PARAMS, classify, scaleFor, type Param } from "../lib/params";
import { formatValue, plural } from "@core/lib/format";
import ShareCard from "@core/components/ShareCard";

interface Props {
  station: Station;
  rows: SampleRow[];
  param: Param;
  terciles: [number, number] | null;
  /** Whether the relative ranking is against the filtered scope or every
   *  station, so the panel can say which comparison it is making. */
  tercileBasis: "scope" | "all" | null;
  tercileN: number;
  url: string;
  waterbodyLink: string;
  onClose: () => void;
  onWaterbody: (wb: string) => void;
  onParam: (key: string) => void;
}

export default function StationDetail({
  station, rows, param, terciles, tercileBasis, tercileN,
  url, waterbodyLink, onClose, onWaterbody, onParam,
}: Props) {
  const [imgFailed, setImgFailed] = useState(false);
  const latest = station.latest[param.key];
  const rating = classify(param, latest?.v, terciles);
  const scale = scaleFor(param);
  const r = scale[rating];

  // Which depth classes this station actually has, so the chart key below
  // does not advertise layers that were never sampled.
  const depths = DEPTHS.filter((d) => rows.some((x) => x.c === d));
  // Every parameter this station has ever recorded, newest value first.
  const measured = PARAMS.filter((p) => station.latest[p.key]);

  return (
    <aside className="detail" aria-label={`Details for ${station.siteId}`}>
      <header className="detail-head">
        <span className="detail-id">
          <i className="detail-dot" style={{ background: r.color }} />
          {station.siteId}
        </span>
        <button className="icon-btn" onClick={onClose} aria-label="Close station details">
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" fill="none" />
          </svg>
        </button>
      </header>

      <h2 className="detail-name">{station.name}</h2>
      {station.ambiguousId && (
        <p className="detail-warn">
          Station ID <b>{station.siteId}</b> is used by more than one record in
          Airtable. This link addresses this record specifically.
        </p>
      )}
      <p className="detail-sub">
        <a href={waterbodyLink} onClick={(e) => { e.preventDefault(); onWaterbody(station.waterbody); }}>
          {station.waterbody}
        </a>
        {` · ${plural(station.samples, "sample")}`}
        {station.firstYear && station.lastYear && ` · ${station.firstYear}–${station.lastYear}`}
      </p>

      {station.imageUrl && !imgFailed && (
        <figure className="shot">
          <img
            src={station.imageUrl}
            alt={`Monitoring site ${station.siteId}, ${station.name}`}
            loading="lazy" decoding="async"
            onError={() => setImgFailed(true)}
          />
        </figure>
      )}

      <div className="reading" style={{ background: r.soft }}>
        <div className="reading-head">
          <span className="pill" style={{ background: r.color }}>{r.label}</span>
          {latest && (
            <span className="reading-when">{latest.y} · {latest.d.toLowerCase()}</span>
          )}
        </div>
        <div className="reading-label">{param.label}</div>
        <div className="reading-value" style={{ color: r.color }}>
          {formatValue(latest?.v ?? null, param.digits)}
        </div>
        {param.unit && <div className="reading-unit">{param.unit}</div>}
        {param.judgement === "relative" ? (
          <p className="reading-blurb">
            No applicable standard for this parameter, so this is a comparison
            against{" "}
            {tercileBasis === "all"
              ? `all ${tercileN} monitoring stations`
              : `the ${tercileN} stations currently shown`}
            {" "}— not a judgement of water quality.
          </p>
        ) : (
          <p className="reading-blurb">
            {param.note ? param.note + " " : ""}
            {param.source && <span className="reading-src">Standard: {param.source}.</span>}
          </p>
        )}
      </div>

      {measured.length > 0 && (
        <div className="paramgrid">
          <h3>Most recent by parameter</h3>
          <ul>
            {measured.map((p) => {
              const l = station.latest[p.key]!;
              const on = p.key === param.key;
              return (
                <li key={p.key}>
                  <button
                    className={"paramcell" + (on ? " on" : "")}
                    onClick={() => onParam(p.key)}
                    aria-pressed={on}
                    title={`Show ${p.label} on the map and chart`}
                  >
                    <span className="pc-label">{p.short}</span>
                    <span className="pc-value">
                      {formatValue(l.v, p.digits)}
                      {p.unit && <span className="unit">{p.unit}</span>}
                    </span>
                    <span className="pc-year">{l.y}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {depths.length > 0 && (
        <p className="detail-sub">
          Sampled at{" "}
          {depths.map((d, i) => (
            <span key={d}>
              {i > 0 && (i === depths.length - 1 ? " and " : ", ")}
              <b style={{ color: DEPTH_COLOR[d] }}>{d.toLowerCase()}</b>
            </span>
          ))}
          .
        </p>
      )}

      {station.description && <p className="detail-note">{station.description}</p>}

      <ShareCard
        url={url}
        filename={station.key}
        heading="Link to this station"
        blurb="Print the code on site signage — it opens this station's chemistry record."
      />
    </aside>
  );
}
